import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { artistSchemas } from '@/api/artists/artist.schema';
import { Button } from '@/components/ui/button';
import { Content } from '@/components/ui/content';
import { Form, FormContent, FormFooter } from '@/components/ui/form';
import { FormInput } from '@/components/ui/form-fields/form-input';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { Icon } from '@/components/ui/icon';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { useCreateArtist } from '@/services/artists';
import type { ValidationError } from '@/types/api/validation-error';

export const Route = createFileRoute('/_app/artists/create')({
  head: () => meta({ label: 'Novo artista' }),
  component: RouteComponent,
});

type Schema = import('zod').input<typeof artistSchemas.create>;

function RouteComponent() {
  const router = useRouter();
  const { mutate: save, error, isSuccess, isPending: isSubmitting } = useCreateArtist();

  const form = useForm<Schema>({
    resolver: zodResolver(artistSchemas.create) as any,
    shouldUseNativeValidation: false,
    defaultValues: { name: '', isActive: true },
  });

  const handleSubmit = (data: Schema) => save(data as any);

  useEffect(() => {
    if (isSuccess) router.back();
  }, [isSuccess, router]);

  useEffect(() => {
    if (error?.status === 422) {
      const { errors } = error.value as unknown as ValidationError<Schema>;

      if (errors) {
        for (const [key, message] of Object.entries(errors)) {
          form.setError(key as keyof Schema, { message: message as string });
        }
      }
    }
  }, [error, form]);

  return (
    <Content
      breadcrumbs={{
        icon: 'mic-vocal',
        title: 'Novo artista',
        items: [
          { label: 'Listagem', route: '/artists' },
          { label: 'Novo', active: true },
        ],
      }}
    >
      <Form
        id="form"
        title="Dados do artista"
        description="Artista inativo continua na listagem, mas não aparece ao escolher o artista de uma cifra."
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormContent>
          <FormInput name="name" control={form.control} label="Nome" required />
          <FormSwitch name="isActive" control={form.control} label="Ativo" />
        </FormContent>
        <FormFooter>
          <Button
            type="button"
            disabled={isSubmitting}
            variant="outline"
            onClick={() => form.reset()}
          >
            Limpar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="success"
            form="form"
            className="flex items-center gap-2"
          >
            <Icon name="save" />
            Salvar
          </Button>
        </FormFooter>
      </Form>
    </Content>
  );
}
